// Analysis profit-breakdown widgets shared by the Vertical Spreads and 0DTE
// pages: a realized-P&L table grouped by date (with weekday) and a weekday
// histogram. VS splits entry vs exit; 0DTE opens and closes the same day, so it
// renders a single date table and a single weekday histogram.

export type ProfitTrade = { openedDate: string; closedDate: string; earlyPnl: number };

const WEEKDAY_ABBR = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
/** True for a real calendar ISO date (YYYY-MM-DD). Guard empty/invalid dates so
 *  they never land in date/weekday buckets (empty string sorts first and
 *  formats as NaN/NaN/NaN). Analysis is closed-only; this is defensive. */
const isIsoDate = (iso: string | null | undefined): iso is string =>
  !!iso && /^\d{4}-\d{2}-\d{2}/.test(iso);
const isoWeekday = (iso: string) => WEEKDAY_ABBR[new Date(iso + "T00:00:00").getDay()];
const pnlUsd = (n: number) =>
  (n < 0 ? "-$" : "$") + Math.abs(n).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const fmtShortDate = (iso: string) => {
  const d = new Date(iso + "T00:00:00");
  if (Number.isNaN(d.getTime())) return "—";
  return `${d.getMonth() + 1}/${d.getDate()}/${String(d.getFullYear()).slice(-2)}`;
};

// Realized P&L grouped by each position's entry (openedDate) or exit (closedDate)
// date, one row per date with its weekday. Open trades (empty closedDate) are
// excluded when grouping by exit day so the first row is never NaN/NaN/NaN.
export function ProfitByDateTable({ trades, dateKey, label, verb }: {
  trades: ProfitTrade[];
  dateKey: "openedDate" | "closedDate";
  label: string;
  verb: string;
}) {
  const dated = trades.filter((t) => isIsoDate(t[dateKey]));
  const byDay = new Map<string, { pnl: number; count: number }>();
  for (const t of dated) {
    const day = t[dateKey];
    const e = byDay.get(day) ?? { pnl: 0, count: 0 };
    e.pnl += t.earlyPnl;
    e.count += 1;
    byDay.set(day, e);
  }
  // Newest first — matches Per-trade detail reverse-chronological default.
  const days = [...byDay.entries()].sort(([d1], [d2]) => (d1 < d2 ? 1 : -1));
  const total = dated.reduce((sum, t) => sum + t.earlyPnl, 0);
  return (
    <div className="shrink-0">
      <div className="text-amber text-xs font-semibold mb-1.5">{label}</div>
      <div className="overflow-auto max-h-[360px]">
        <table className="text-xs whitespace-nowrap">
          <thead>
            <tr className="text-amber border-b border-border">
              <th className="py-1 pr-6 text-left underline">Date</th>
              <th className="py-1 pr-6 text-left underline">Day</th>
              <th className="py-1 pr-6 text-center underline">{verb}</th>
              <th className="py-1 pr-4 text-right underline">P&L</th>
            </tr>
          </thead>
          <tbody>
            {days.map(([date, d]) => (
              <tr key={date} className="border-b border-border/40">
                <td className="py-1 pr-6 font-mono">{fmtShortDate(date)}</td>
                <td className="py-1 pr-6 font-mono text-muted">{isoWeekday(date)}</td>
                <td className="py-1 pr-6 text-center font-mono">{d.count}</td>
                <td className={`py-1 pr-4 text-right font-mono ${d.pnl >= 0 ? "text-green" : "text-red"}`}>{pnlUsd(d.pnl)}</td>
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr className="border-t-2 border-border text-amber font-semibold">
              <td className="py-1 pr-6" colSpan={2}>TOTAL</td>
              <td className="py-1 pr-6 text-center font-mono">{dated.length}</td>
              <td className={`py-1 pr-4 text-right font-mono ${total >= 0 ? "text-green" : "text-red"}`}>{pnlUsd(total)}</td>
            </tr>
          </tfoot>
        </table>
      </div>
    </div>
  );
}

// Horizontal-bar histogram of per-trade profit edge by weekday (Mon–Fri) for
// positions entered or exited on that weekday: each day's share of total profit
// divided by its trade count. Dividing out trade count strips trading frequency,
// so a genuinely better DAY stands out. Bar length ∝ |value|, green/red by sign;
// trade count sits to the left, the value to the right.
export function WeekdayProfitHistogram({ trades, dateKey, label }: {
  trades: ProfitTrade[];
  dateKey: "openedDate" | "closedDate";
  label: string;
}) {
  const buckets = [1, 2, 3, 4, 5].map((wd) => ({ name: WEEKDAY_ABBR[wd], pnl: 0, count: 0 }));
  for (const t of trades) {
    if (!isIsoDate(t[dateKey])) continue;
    const wd = new Date(t[dateKey] + "T00:00:00").getDay();
    if (wd >= 1 && wd <= 5) {
      buckets[wd - 1].pnl += t.earlyPnl;
      buckets[wd - 1].count += 1;
    }
  }
  const totalPnl = buckets.reduce((sum, b) => sum + b.pnl, 0);
  // Share of total profit per trade (% ÷ count). Guard zero denominators.
  const pnlPct = (v: number) => (totalPnl !== 0 ? (v / totalPnl) * 100 : 0);
  const perTradePct = (b: { pnl: number; count: number }) => (b.count > 0 ? pnlPct(b.pnl) / b.count : 0);
  const maxPerTrade = Math.max(0.01, ...buckets.map((b) => Math.abs(perTradePct(b))));
  return (
    <div className="shrink-0 w-full mb-[30px]">
      <div className="text-amber text-xs font-semibold mb-1.5">{label}</div>
      <div className="flex flex-col gap-1.5">
        {buckets.map((b) => {
          const perTrade = perTradePct(b);
          return (
            <div key={b.name} className="flex items-center gap-2 text-xs font-mono">
              <span className="w-8 text-muted">{b.name}</span>
              <span className="w-9 text-right text-muted">{b.count}×</span>
              <div className="flex-1">
                <div
                  className={`h-4 rounded-sm ${perTrade >= 0 ? "bg-green/70" : "bg-red/70"}`}
                  style={{ width: `${(Math.abs(perTrade) / maxPerTrade) * 100}%`, minWidth: b.count > 0 ? "2px" : "0" }}
                  title={`${perTrade.toFixed(1)}% of profit per trade`}
                />
              </div>
              <span className={`w-24 text-right ${perTrade >= 0 ? "text-green" : "text-red"}`}>{perTrade.toFixed(1)}%/tr</span>
            </div>
          );
        })}
      </div>
    </div>
  );
}
